import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, Edit, Trash2, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ApiPropertyType } from '@/types/api';

interface PropertyTypeCardProps {
  propertyType: ApiPropertyType;
  iconMap: Record<string, LucideIcon>;
  colorMap: Record<string, string>;
  onEdit?: (propertyType: ApiPropertyType) => void;
  onDelete?: (propertyType: ApiPropertyType) => void;
}

export function PropertyTypeCard({
  propertyType,
  iconMap,
  colorMap,
  onEdit,
  onDelete,
}: PropertyTypeCardProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  const IconComponent = propertyType.icon_name
    ? iconMap[propertyType.icon_name]
    : Building2;
  const colorGradient = propertyType.color
    ? colorMap[propertyType.color]
    : 'from-gray-500 to-gray-600';

  return (
    <Card
      className="cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105 group relative overflow-hidden"
      onClick={() => navigate(`/admin/templates/${propertyType.id}`)}
    >
      <CardHeader className="pb-4">
        <div
          className={cn(
            'flex items-center gap-4',
            isRTL ? 'flex-row-reverse' : 'flex-row'
          )}
        >
          <div
            className={cn(
              'p-4 rounded-xl transition-all duration-300 bg-gradient-to-br',
              colorGradient,
              'text-white shadow-lg group-hover:scale-110'
            )}
          >
            {IconComponent && <IconComponent className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <div
              className={cn(
                'flex items-center justify-between gap-2',
                isRTL ? 'flex-row-reverse' : 'flex-row'
              )}
            >
              <div className={cn(isRTL && 'text-right')}>
                <CardTitle className="text-xl font-bold">
                  {propertyType.name}
                </CardTitle>
              </div>
              <div
                className={cn(
                  'flex items-center gap-1',
                  isRTL ? 'flex-row-reverse' : 'flex-row'
                )}
              >
                {onEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation();
                      onEdit(propertyType);
                    }}
                    className="hover:bg-primary/10"
                    title="Edit property type"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                )}
                {onDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation();
                      onDelete(propertyType);
                    }}
                    className="hover:bg-destructive/10 hover:text-destructive"
                    title="Delete property type"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div
          className={cn(
            'text-center p-6 bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl',
            isRTL && 'text-right'
          )}
        >
          <div className="text-3xl font-bold text-primary mb-2">
            {propertyType.standards_count || 0}
          </div>
          <div className="text-sm font-medium text-muted-foreground">
            {t('templates.admin.management.standards')}
          </div>
        </div>

        <Button
          className="w-full gap-2 group-hover:bg-primary group-hover:text-primary-foreground"
          variant="outline"
          onClick={e => {
            e.stopPropagation();
            navigate(`/admin/templates/${propertyType.id}`);
          }}
        >
          {t('templates.admin.management.manageTemplates')}
        </Button>
      </CardContent>
    </Card>
  );
}
